import React from 'react';
import { useConfiguration } from '../../context/configurationContext';
import { types as sdkTypes } from '../../util/sdkLoader';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import css from './OrderBreakdown.module.css';

/**
 * A component that renders the total price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction - The transaction to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl } = props;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);
  const isCompleted = process.isCompleted(transaction?.attributes?.lastTransition);
  const isRefunded = process.isRefunded(transaction?.attributes?.lastTransition);
  const { refundResult = null } = transaction?.attributes?.metadata || {};

  const config = useConfiguration();
  const { currency } = config;
  const { Money } = sdkTypes;

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : (
    <FormattedMessage id="OrderBreakdown.total" />
  );

  const getTotalWithRefund = ({ payinTotal, payoutTotal, refundResult, isProvider }) => {
    if (isProvider) {
      if (!refundResult || (refundResult.amount === 0 && refundResult.refundFee === 0)) {
        return payoutTotal;
      }

      let newTotal;

      if (refundResult.amount === payinTotal.amount) {
        newTotal = 0;
      } else {
        newTotal = refundResult.amount - refundResult.refundFee;
      }

      return new Money(newTotal, currency);
    } else {
      if (!refundResult) return payinTotal;

      const newTotal = payinTotal.amount - refundResult.amount;

      return new Money(newTotal, currency);
    }
  };

  const totalPrice = getTotalWithRefund({
    payinTotal: transaction.attributes.payinTotal,
    payoutTotal: transaction.attributes.payoutTotal,
    refundResult,
    isProvider,
  });
  const formattedTotalPrice = formatMoney(intl, totalPrice);

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

export default LineItemTotalPrice;
